import { ChipAnalysisContent } from "./ChipAnalysisContent";
import { CHIP_RESULT_ANALYSES } from "../resultOrderSelection";
import { XrfAnalysisContent } from "./XrfAnalysisContent";
import React from "react";
import { SampleAnalysisContent } from "./SampleAnalysisContent";
import { Switch } from "@fluentui/react-components";
import type { FileReference } from "../../OiReport";
import {
  AnalysisResultCompletion,
  AnalysisResultSection,
  AnalysisResultStatus,
  AnalysisResultSummaryRow,
} from "../../../../services/AnalysisResultService";
import { normalizeSamples } from "../utils";
import { HorizontalScrollArea } from "./HorizontalScrollArea";
import {
  canCompleteSection,
  inputClassName,
  labelClassName,
  ResultCompletionDatePicker,
  SAMPLE_CELL_CLASSNAME,
  SAMPLE_COLUMN_WIDTH_CLASS,
  tableCellClassName,
  tableHeaderClassName,
} from "./shared";

type NormalizedSample = ReturnType<typeof normalizeSamples>[number];

export interface AnalysisSectionContentProps {
  section: AnalysisResultSection;
  densityMeasurementTemperature?: string;
  densityCorrectionFactor?: string;
  onDensitySettingsChange?: (field: "densityMeasurementTemperature" | "densityCorrectionFactor", value: string) => void;
  /** Calculated per-sample results (CoM, MM) shown read-only in the Hc / MM / Density table. */
  summaryRows?: AnalysisResultSummaryRow[];
  normalizedSamples: NormalizedSample[];
  uploadingSectionKey: string | null;
  sectionFileInputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  onUpdateSectionRowCell: (analysisKey: string, rowIndex: number, sampleId: string, value: string) => void;
  onUpdateSection: (analysisKey: string, updater: (section: AnalysisResultSection) => AnalysisResultSection) => void;
  onAddXrfElement: () => void;
  onRemoveXrfElement: (symbol: string) => void;
  onSectionFileSelect: (analysisKey: string) => void;
  onHandleSectionFileUpload: (analysisKey: string, files: File[]) => Promise<void>;
  onHandleRowFileSelect: (rowKey: string) => void;
  onHandleSemRowFileUpload: (analysisKey: string, rowLabel: string, files: File[]) => Promise<void>;
  onRemoveSectionFile: (analysisKey: string, fileUuid: string) => void;
  onRemoveSemRowFile: (analysisKey: string, rowLabel: string, fileUuid: string) => void;
  onSetDragOverSectionKey: React.Dispatch<React.SetStateAction<string | null>>;
  onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
  /** Completed results are shown read-only: values can be read and files opened, nothing can be changed. */
  readOnly?: boolean;
}

export function AnalysisSectionContent(props: AnalysisSectionContentProps) {
  const {
  section,
  normalizedSamples,
  uploadingSectionKey,
  sectionFileInputRefs,
  onUpdateSectionRowCell,
  onUpdateSection,
  onSectionFileSelect,
  onHandleSectionFileUpload,
  onRemoveSectionFile,
  onSetDragOverSectionKey,
  onOpenFileGallery,
  readOnly = false,
} = props;
  if (CHIP_RESULT_ANALYSES.includes(section.analysisKey)) return <ChipAnalysisContent {...props} />;
  if (["other"].includes(section.analysisKey)) return <SampleAnalysisContent {...props} showSampleTable={false} />;
  if (section.analysisKey === "xrf") return <XrfAnalysisContent {...props} />;
  if (["carbon", "hardness", "hcMmDensity", "oxygenNitrogen"].includes(section.analysisKey)) return <SampleAnalysisContent {...props} />;
  const emptyColumns = Array.from({ length: Math.max(0, 7 - normalizedSamples.length) }, (_, index) => index);

  const renderAnalysisGrid = () => {
    return (
      <HorizontalScrollArea scrollAreaId={`analysis-result-${section.analysisKey}-grid`}>
        <table className="w-full min-w-[720px] table-fixed border-collapse bg-primary-dark/5 dark:bg-black/20 text-left [&_th]:border [&_th]:border-primary-dark/5 dark:[&_th]:border-white/5 [&_th]:font-normal [&_td]:border [&_td]:border-primary-dark/5 dark:[&_td]:border-white/5">
          <colgroup>
            <col className={SAMPLE_COLUMN_WIDTH_CLASS} />
            {normalizedSamples.map(sample => <col key={`${section.analysisKey}-col-${sample.sampleId}`} className={SAMPLE_COLUMN_WIDTH_CLASS} />)}
            {emptyColumns.map(index => <col key={`empty-${index}`} className="w-[74px]" />)}
          </colgroup>
          <thead className={tableHeaderClassName}>
            <tr>
              <th className="px-2 py-1">{section.analysisKey === "xrf" ? "Element" : "Sample"}</th>
              {section.rows[0]?.rowLabel && section.analysisKey !== "xrf" && section.analysisKey !== "carbon" && section.analysisKey !== "hardness" && section.analysisKey !== "hcMmDensity" && <th className="px-2 py-1">Value</th>}
              {normalizedSamples.map(sample => <th key={`${section.analysisKey}-${sample.sampleId}`} className="px-2 py-1">{sample.sampleId}</th>)}
              {emptyColumns.map(index => <th key={`empty-${index}`} aria-hidden="true" />)}
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, rowIndex) => (
              <tr key={`${section.analysisKey}-${row.rowLabel}-${rowIndex}`}>
                <td className={`border-t border-primary-dark/5 dark:border-white/5 px-2 py-1 text-[12px] text-primary-dark/85 dark:text-white/85 ${SAMPLE_CELL_CLASSNAME}`}>{row.rowLabel}</td>
                {normalizedSamples.map(sample => (
                  <td key={`${section.analysisKey}-${row.rowLabel}-${sample.sampleId}`} className={tableCellClassName}>
                    <input
                      disabled={readOnly}
                      className={inputClassName}
                      placeholder="-"
                      value={row.sampleValues[sample.sampleId] || ""}
                      onChange={event => onUpdateSectionRowCell(section.analysisKey, rowIndex, sample.sampleId, event.target.value)}
                    />
                  </td>
                ))}
                {emptyColumns.map(index => <td key={`empty-${index}`} aria-hidden="true" />)}
              </tr>
            ))}
          </tbody>
        </table>
      </HorizontalScrollArea>
    );
  };

  return (
    <div className="px-2 pb-3">
      {renderAnalysisGrid()}

      {section.analysisKey !== "sem" && (
        <div className="mt-3 grid gap-3 grid-cols-[100px,1fr] md:items-center">
          <div className={labelClassName}>Attach files</div>
          <div>
            <input
              ref={element => {
                sectionFileInputRefs.current[section.analysisKey] = element;
              }}
              type="file"
              multiple
              accept="*/*"
              className="hidden"
              onChange={event => {
                const selectedFiles = Array.from(event.target.files ?? []);
                event.target.value = "";
                void onHandleSectionFileUpload(section.analysisKey, selectedFiles);
              }}
            />
            {uploadingSectionKey === section.analysisKey && (
              <div className="mb-2 rounded-[3px] border border-oi-button-blue/70 bg-oi-button-blue/10 px-2 py-1 text-[12px] text-primary-dark dark:text-primary-light">
                Uploading files...
              </div>
            )}
            <div
              className="flex min-h-7 flex-wrap items-center gap-x-5 gap-y-2 px-0 py-1 text-[12px] text-primary-dark/80 dark:text-white/80"
              onDragEnter={event => {
                event.preventDefault();
                event.stopPropagation();
                onSetDragOverSectionKey(section.analysisKey);
              }}
              onDragLeave={event => {
                event.preventDefault();
                event.stopPropagation();
                onSetDragOverSectionKey(current => current === section.analysisKey ? null : current);
              }}
              onDragOver={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onDrop={event => {
                event.preventDefault();
                event.stopPropagation();
                onSetDragOverSectionKey(current => current === section.analysisKey ? null : current);
                if (readOnly) return;
                void onHandleSectionFileUpload(section.analysisKey, Array.from(event.dataTransfer.files ?? []));
              }}
            >
              {section.files.map((file, index) => (
                <div key={`${section.analysisKey}-${file.uuid}`} className="inline-flex items-center gap-2">
                  {!readOnly && <button
                    type="button"
                    className="flex h-4 w-4 items-center justify-center rounded-[2px] bg-oi-button-blue text-[11px] font-bold leading-none text-primary-dark dark:text-white"
                    title={`Remove ${file.fileName}`}
                    onClick={() => onRemoveSectionFile(section.analysisKey, file.uuid)}
                  >
                    ✓
                  </button>}
                  <button
                    type="button"
                    className="bg-transparent p-0 text-left text-[10px] text-primary-dark/90 dark:text-white/90 hover:text-primary-dark dark:hover:text-white"
                    onClick={() => onOpenFileGallery?.(section.files, index)}
                  >
                    {file.fileName}
                  </button>
                </div>
              ))}
              {!readOnly && <button
                type="button"
                className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-light dark:bg-white/90 text-[18px] leading-none text-primary-dark dark:text-oi-dark-background hover:bg-white"
                onClick={() => onSectionFileSelect(section.analysisKey)}
                aria-label="Upload files"
              >
                +
              </button>}
            </div>
          </div>
        </div>
      )}

      {section.analysisKey !== "sem" && (
        <div className="mt-2 grid gap-3 grid-cols-[100px,minmax(0,232px)] md:items-center">
          <div className={labelClassName}>Comment</div>
          <input disabled={readOnly} className="h-6 border border-primary-dark/20 dark:border-white/20 bg-white dark:bg-oi-dark-background px-3 text-[12px] text-oi-button-blue dark:text-oi-yellow outline-none" placeholder="-" value={section.comment} onChange={event => onUpdateSection(section.analysisKey, current => ({ ...current, comment: event.target.value, status: AnalysisResultStatus.InProgress }))} />
        </div>
      )}

      <div className="mt-3 grid gap-2 border-t border-primary-dark/25 dark:border-white/25 pt-2 xl:grid-cols-[200px,minmax(0,1fr),24px,90px] md:items-center">
        <div className="flex items-center gap-1 text-[10px] text-primary-dark/90 dark:text-white/90">
          <span>Complete analysis *</span>
          <Switch
            disabled={readOnly || (!canCompleteSection(section) && !section.completion.isComplete)}
            title={!canCompleteSection(section) ? "Enter completed by and date to complete" : undefined}
            checked={section.completion.isComplete}
            onChange={(_, data) => onUpdateSection(section.analysisKey, current => ({
              ...current,
              completion: {
                ...current.completion,
                isComplete: data.checked,
              } as AnalysisResultCompletion,
              status: data.checked ? AnalysisResultStatus.Completed : AnalysisResultStatus.InProgress,
            }))}
          />
        </div>
        <div className="grid gap-2 md:grid-cols-[96px,minmax(0,1fr)] md:items-center">
          <div className="text-[10px] text-primary-dark/90 dark:text-white/90">Analysis completed by *</div>
          <input disabled={readOnly} className="h-6 border border-primary-dark/20 dark:border-white/20 bg-white dark:bg-oi-dark-background px-3 text-[12px] text-oi-button-blue dark:text-oi-yellow outline-none" value={section.completion.completedBy} placeholder="-" onChange={event => onUpdateSection(section.analysisKey, current => ({ ...current, completion: { ...current.completion, completedBy: event.target.value } }))} />
        </div>
        <div className="text-right text-[10px] text-primary-dark/90 dark:text-white/90">Date *</div>
        <ResultCompletionDatePicker
          ariaLabel={`${section.title} completion date`}
          value={section.completion.completedDate}
          disabled={readOnly}
          onChange={completedDate => onUpdateSection(section.analysisKey, current => ({
            ...current,
            completion: {
              ...current.completion,
              completedDate,
            },
          }))}
        />
      </div>

    </div>
  );
}
